import { useEffect, useMemo, useRef, useState } from "react";

const RESET_FADE_MS = 320;

interface Props {
  text: string;
}

/**
 * Karaoke-style scrolling caption. Each new word appears at the bottom of the
 * frame and the accumulated text scrolls upward into a perspective-tilted
 * mask. New AI turn → fade out → swap → fade in. Verbatim port.
 */
export function LiveCaption({ text }: Props) {
  const [shown, setShown] = useState(text);
  const [phase, setPhase] = useState<"in" | "out">("in");
  const prevTextRef = useRef(text);

  useEffect(() => {
    const prev = prevTextRef.current;
    const next = text;
    prevTextRef.current = next;
    if (next === prev) return;

    if (next.startsWith(prev)) {
      setShown(next);
      setPhase("in");
      return;
    }

    setPhase("out");
    const t = setTimeout(() => {
      setShown(next);
      setPhase("in");
    }, RESET_FADE_MS);
    return () => clearTimeout(t);
  }, [text]);

  const tokens = useMemo(() => {
    if (!shown) return [] as { key: string; text: string; isWord: boolean }[];
    return shown.split(/(\s+)/).map((t, i) => ({
      key: `${i}-${t}`,
      text: t,
      isWord: /\S/.test(t),
    }));
  }, [shown]);

  if (!shown) return null;

  return (
    // h-full: the height is owned by the page's caption slot (110px mobile /
    // 170px desktop in Interview.tsx). A hardcoded 170 here used to push the
    // bottom-anchored text 60px below the slot on phones — straight over the
    // Pause/End buttons.
    <div
      className={`relative w-full h-full max-w-[640px] mx-auto transition-opacity duration-300 ease-out ${
        phase === "out" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        maskImage:
          "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 15%, #000 40%)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 15%, #000 40%)",
      }}
    >
      <div className="absolute inset-x-2 bottom-0">
        <p className="text-center text-[18px] sm:text-[20px] leading-[1.45] font-medium text-brand-700">
          {tokens.map(({ key, text: t, isWord }) =>
            isWord ? (
              // NO will-change here: it promoted every word into its own
              // compositing layer, and iOS Safari renders those layer edges
              // as faint boxes around each word (amplified by the parent's
              // mask gradient). The animation is plain opacity/translate —
              // it doesn't need layer promotion.
              <span key={key} className="inline-block animate-karaoke-in">
                {t}
              </span>
            ) : (
              <span key={key}>{t}</span>
            ),
          )}
        </p>
      </div>
    </div>
  );
}
